import { useEffect, useRef, useState } from "react";
import { ArrowLeftRight } from "lucide-react";

export const BeforeAfterSlider = ({ beforeSrc, afterSrc, beforeLabel = "BEFORE", afterLabel = "AI PREVIEW" }) => {
  const wrapRef = useRef(null);
  const [pos, setPos] = useState(50); // percent
  const draggingRef = useRef(false);

  const setFromClientX = (clientX) => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const p = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    setPos(p);
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!draggingRef.current) return;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      setFromClientX(x);
    };
    const stop = () => (draggingRef.current = false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("mouseup", stop);
    window.addEventListener("touchend", stop);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("mouseup", stop);
      window.removeEventListener("touchend", stop);
    };
  }, []);

  return (
    <div
      ref={wrapRef}
      data-testid="before-after-slider"
      className="relative w-full aspect-square sm:aspect-[4/3] rounded-3xl overflow-hidden border border-[#eae4d9] bg-[#f3efe6] select-none"
      onMouseDown={(e) => {
        draggingRef.current = true;
        setFromClientX(e.clientX);
      }}
      onTouchStart={(e) => {
        draggingRef.current = true;
        setFromClientX(e.touches[0].clientX);
      }}
    >
      <img
        src={afterSrc}
        alt="AI preview"
        className="absolute inset-0 w-full h-full object-cover"
        draggable={false}
      />
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${pos}%` }}
      >
        <img
          src={beforeSrc}
          alt="Before"
          className="absolute inset-0 h-full object-cover"
          style={{ width: `${wrapRef.current?.offsetWidth || 0}px`, maxWidth: "none" }}
          draggable={false}
        />
      </div>

      <span className="absolute top-3 left-3 rounded-full bg-[#0f172a]/85 text-white text-[10px] font-bold tracking-[0.15em] px-3 py-1.5">
        {beforeLabel}
      </span>
      <span className="absolute top-3 right-3 rounded-full bg-[#ff6b4a] text-white text-[10px] font-bold tracking-[0.15em] px-3 py-1.5">
        {afterLabel}
      </span>

      <div
        className="absolute top-0 bottom-0 w-0.5 bg-white shadow-[0_0_0_1px_rgba(15,23,42,0.15)]"
        style={{ left: `calc(${pos}% - 1px)` }}
      >
        <button
          data-testid="slider-handle"
          aria-label="Drag to compare"
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-11 h-11 rounded-full bg-white shadow-lg border border-[#eae4d9] flex items-center justify-center comparison-handle"
        >
          <ArrowLeftRight className="w-4 h-4 text-[#0f172a]" />
        </button>
      </div>
    </div>
  );
};

export default BeforeAfterSlider;
